import React, { forwardRef, useRef } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import "./NewsletterPreview.css";
import LogoAN from "../../assets/Alerta Normativa-logo2026-blanco.png";
import LogoAN2 from "../../assets/Alerta Normativa-logo2026-claro.png";
import LogoAustranet2 from "../../assets/Austranet-logo-2.png";
import LogoAustranet1 from "../../assets/Austranet-logo-1.png";
import FondoNewsletter from "../../assets/FondoNewsletter.png";
import FondoNewsletter2 from "../../assets/FondoNewsletter3.png";
import LogoDarCumplimiento from "../../assets/logo-darCumplimiento.png";

const ICONOS = {
  banco: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M3 21h18M3 10h18M5 10V21M19 10V21M12 10V21M2 10l10-7 10 7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  ministerio: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 22V12h6v10" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  regulador: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  bolsa: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="16 7 22 7 22 13" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  seguro: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  pension: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="7" r="4" stroke="#fff" strokeWidth="1.5"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  default: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="8" y1="13" x2="16" y2="13" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="8" y1="17" x2="13" y2="17" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

function getIcono(sigla = "") {
  const s = sigla.toUpperCase();
  if (s.includes("BC") || s.includes("BCH") || s.includes("BANCO")) return ICONOS.banco;
  if (s.includes("MIN") || s.includes("MH") || s.includes("MT")) return ICONOS.ministerio;
  if (s.includes("CMF") || s.includes("SVS") || s.includes("SBIF") || s.includes("SBCH")) return ICONOS.regulador;
  if (s.includes("BVM") || s.includes("BOLSA")) return ICONOS.bolsa;
  if (s.includes("SEG") || s.includes("AAC")) return ICONOS.seguro;
  if (s.includes("SP") || s.includes("AFP") || s.includes("PENSION")) return ICONOS.pension;
  return ICONOS.default;
}

const makeNormaUid = (organismoId, norma) =>
  `norma_${organismoId}_${norma.id ?? norma.nombreAbreviado}_${norma.fechaPublicacion}`;

const makeNoticiaUid = (organismoId, norma) =>
  `noticia_${organismoId}_${norma.id ?? norma.nombreAbreviado}_${norma.fechaPublicacion}`;

const EditableField = ({ value, onChange, className, placeholder }) => {
  const ref = useRef(null);
  const aplicarFormato = (cmd) => { ref.current?.focus(); document.execCommand(cmd); };
  return (
    <div>
      <div className="nl-format-bar nl-no-print">
        <button onMouseDown={(e) => { e.preventDefault(); aplicarFormato("bold"); }}><b>N</b></button>
        <button onMouseDown={(e) => { e.preventDefault(); aplicarFormato("underline"); }}><u>S</u></button>
      </div>
      <div
        ref={ref}
        className={className}
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange(e.currentTarget.innerHTML)}
        dangerouslySetInnerHTML={{ __html: value }}
        data-placeholder={placeholder}
        style={{ outline: "none", cursor: "text" }}
      />
    </div>
  );
};

const NormasTable = ({ normas, onEliminar, onEditarCelda }) => (
  <table className="nl-table">
    <thead>
      <tr>
        <th style={{ width: "25%" }}>Norma legal</th>
        <th style={{ width: "15%", textAlign: "center" }}>Fecha</th>
        <th style={{ width: "42%" }}>Contenido</th>
        <th style={{ width: "18%" }}>Vigencia</th>
        <th className="nl-th-actions nl-no-print" style={{ width: "32px" }} />
      </tr>
    </thead>
    <tbody>
      {normas.map((norma, nIdx) => (
        <tr key={nIdx} className={nIdx % 2 === 0 ? "row-even" : "row-odd"}>
          <td className="nl-table-norma nl-cell-editable">
            {norma.url ? (
              <a
                href={norma.url}
                target="_blank"
                rel="noopener noreferrer"
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => onEditarCelda(norma, "nombreAbreviado", e.currentTarget.innerText)}
                onClick={(e) => e.preventDefault()}
              >
                {norma.nombreAbreviado}
              </a>
            ) : (
              <span
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => onEditarCelda(norma, "nombreAbreviado", e.currentTarget.innerText)}
              >
                {norma.nombreAbreviado}
              </span>
            )}
          </td>

          <td
            className="nl-cell-editable"
            style={{ textAlign: "center" }}
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => onEditarCelda(norma, "fechaPublicacion", e.currentTarget.innerText)}
          >
            {typeof norma.fechaPublicacion === "string" && norma.fechaPublicacion.match(/^\d{2}-\d{2}-\d{4}$/)
              ? norma.fechaPublicacion
              : format(new Date(norma.fechaPublicacion), "dd-MM-yyyy")}
          </td>

          <td
            className="nl-table-desc nl-cell-editable"
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => onEditarCelda(norma, "descripcionAbreviado", e.currentTarget.innerText)}
            dangerouslySetInnerHTML={{ __html: norma.descripcionAbreviado }}
          />

          <td
            className="nl-table-vigencia nl-cell-editable"
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => onEditarCelda(norma, "vigencia", e.currentTarget.innerText)}
            dangerouslySetInnerHTML={{ __html: norma.vigencia }}
          />

          <td className="nl-td-actions nl-no-print">
            <button className="nl-btn-remove" onClick={() => onEliminar(norma)} title="Quitar esta norma del newsletter">
              ✕
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PaginaNoticias = ({ grupos, eliminadas, onEliminarNoticia }) => {
  const gruposNoticias = grupos
    .map((grupo) => ({
      organismoId: grupo.organismoId,
      organismoSigla: grupo.organismoSigla,
      organismoNombre: grupo.organismoNombre,
      noticias: grupo.normas
        .filter((n) => n.tipoDocumento === "Noticias")
        .filter((n) => !eliminadas.has(makeNoticiaUid(grupo.organismoId, n)))
        .map((n) => ({ ...n, _organismoId: grupo.organismoId })),
    }))
    .filter((g) => g.noticias.length > 0);

  const totalNoticias = gruposNoticias.reduce((acc, g) => acc + g.noticias.length, 0);

  if (!totalNoticias) return null;

  return (
    <div className="nl-page nl-noticias-page">
      <div className="nl-noticias-header">
        <div className="nl-noticias-header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div>
          <div className="nl-noticias-header-title">Noticias de interés</div>
          <div className="nl-noticias-header-sub">
            {totalNoticias} noticia{totalNoticias !== 1 ? "s" : ""} · {gruposNoticias.length} organismo{gruposNoticias.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {gruposNoticias.map((grupo, gIdx) => (
        <div className="nl-noticias-grupo" key={grupo.organismoId}>
          <div className={`nl-noticias-org-sep ${gIdx === 0 ? "nl-noticias-org-sep--first" : ""}`}>
            <div className="nl-noticias-org-sep-icon">{getIcono(grupo.organismoSigla)}</div>
            <div className="nl-noticias-org-sep-nombre">
              <span className="nl-noticias-org-sep-sigla">{grupo.organismoSigla}</span>
              <span className="nl-noticias-org-sep-full">{grupo.organismoNombre}</span>
            </div>
            <div className="nl-noticias-org-sep-count">
              {grupo.noticias.length} noticia{grupo.noticias.length !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="nl-noticias-grid">
            {grupo.noticias.map((n, i) => (
              <div className="nl-noticia-card" key={i}>
                <div className="nl-noticia-body">
                  <div className="nl-noticia-meta">
                    <span className="nl-noticia-fecha">
                      {format(new Date(n.fechaPublicacion), "dd MMM yyyy", { locale: es })}
                    </span>
                    <button
                      className="nl-btn-remove nl-btn-remove--noticia nl-no-print"
                      onClick={() => onEliminarNoticia(makeNoticiaUid(n._organismoId, n))}
                      title="Quitar esta noticia"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="nl-noticia-desc">{n.descripcionAbreviado}</div>
                  {n.url ? (
                    <a href={n.url} target="_blank" rel="noopener noreferrer" className="nl-noticia-link">
                      Ver más
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 4 }}>
                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  ) : (
                    <span className="nl-noticia-link nl-noticia-link-disabled">Sin enlace</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const NewsletterPreview = forwardRef(
  ({ data, descripcion, onDescripcionChange, destacado, onDestacadoChange,
     footer, onFooterChange, fechaDesde, fechaHasta, numeroInforme,
     eliminadas, onEliminar, edicionesCeldas, onEditarCelda }, ref) => {

    const editarCelda = (organismoId, norma, campo, valor) => {
      const uid = makeNormaUid(organismoId, norma);
      onEditarCelda(uid, campo, valor);
    };

    const getCeldaValor = (organismoId, norma, campo) => {
      const uid = makeNormaUid(organismoId, norma);
      return edicionesCeldas[uid]?.[campo] ?? norma[campo];
    };

    const mesAnio = fechaDesde ? format(fechaDesde, "MMMM 'de' yyyy", { locale: es }) : "";

    const gruposConNormas = data.grupos
      .map((g) => ({
        ...g,
        normas: g.normas
          .filter((n) => n.tipoDocumento !== "Noticias")
          .filter((n) => !eliminadas.has(makeNormaUid(g.organismoId, n)))
          .map((n) => ({
            ...n,
            nombreAbreviado: getCeldaValor(g.organismoId, n, "nombreAbreviado"),
            fechaPublicacion: getCeldaValor(g.organismoId, n, "fechaPublicacion"),
            descripcionAbreviado: getCeldaValor(g.organismoId, n, "descripcionAbreviado"),
            vigencia: getCeldaValor(g.organismoId, n, "vigencia"),
          })),
      }))
      .filter((g) => g.normas.length > 0);

    return (
      <div className="nl-wrapper" ref={ref}>
        <div className="nl-page nl-portada">
          <div className="nl-header">
            <div className="nl-header-bg">
              <img src={FondoNewsletter} alt="" className="nl-header-fondo" />
            </div>
            <div className="nl-header-logo-overlay">
              <img src={LogoAN} alt="Logo Alerta Normativa" width="180" height="80" style={{ objectFit: "contain" }} />
            </div>
            <div className="nl-header-bar">
              <div className="nl-header-title">Informe mensual N°{numeroInforme}</div>
              <div className="nl-header-date">Matriz de cumplimiento — {mesAnio}</div>
            </div>
          </div>

          <div className="nl-descripcion">
            <div className="nl-edit-label nl-no-print">Descripción (haz clic para editar)</div>
            <EditableField value={descripcion} onChange={onDescripcionChange} className="nl-descripcion-text" placeholder="Escribe la descripción del newsletter aquí..." />
          </div>

          <div className="nl-destacado">
            <div className="nl-edit-label nl-no-print">Destacado del mes (haz clic para editar)</div>
            {destacado && <div className="nl-destacado-label">Destacado del mes</div>}
            <EditableField value={destacado} onChange={onDestacadoChange} className="nl-destacado-text" placeholder="Escribe el destacado del mes aquí o déjalo vacío..." />
          </div>
        </div>

        {gruposConNormas.map((grupo, index) => (
          <div
            className={`nl-page nl-grupo ${index % 2 === 0 ? "nl-grupo-blue" : "nl-grupo-teal"}`}
            key={grupo.organismoId}
          >
            <div className="nl-grupo-header">
              <div className="nl-grupo-icon">{getIcono(grupo.organismoSigla)}</div>
              <div className="nl-grupo-nombre">
                <span className="nl-grupo-sigla">{grupo.organismoSigla}</span>
                <span className="nl-grupo-nombre-full">{grupo.organismoNombre}</span>
              </div>
              <div className="nl-grupo-badges">
                <span className="nl-grupo-count">
                  {grupo.normas.length} norma{grupo.normas.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <NormasTable
              normas={grupo.normas}
              onEliminar={(norma) => onEliminar(makeNormaUid(grupo.organismoId, norma))}
              onEditarCelda={(norma, campo, valor) => editarCelda(grupo.organismoId, norma, campo, valor)}
            />
          </div>
        ))}

        <PaginaNoticias
          grupos={data.grupos}
          eliminadas={eliminadas}
          onEliminarNoticia={onEliminar}
        />

        <div className="nl-page nl-footer-page">
          <div className="nl-footer-header">
            <div className="nl-footer-header-bg">
              <img src={FondoNewsletter2} alt="" className="nl-footer-header-fondo" />
            </div>
            <div className="nl-footer-header-logo-overlay">
              <img src={LogoAN} alt="Logo Alerta Normativa" width="180" height="80" style={{ objectFit: "contain" }} />
            </div>
          </div>

          <div className="nl-footer">
            <div className="nl-edit-label nl-no-print">Pie de página (haz clic para editar)</div>
            <EditableField value={footer} onChange={onFooterChange} className="nl-footer-text" placeholder="Escribe el pie de página aquí..." />
            <div className="nl-footer-logos">
              <img src={LogoDarCumplimiento} alt="Logo Dar Cumplimiento" className="nl-footer-logo" width="180" />
              <img src={LogoAustranet2} alt="Logo Austranet" className="nl-footer-logo" width="140" />
              <img src={LogoAN2} alt="Logo Alerta Normativa" className="nl-footer-logo" width="120" />
            </div>
          </div>

          <div className="nl-footer-spacer" />

          <div className="nl-bottom-bar">
            <div>
              <img src={LogoAustranet1} alt="Logo Austranet" className="nl-bottom-logo" width="120" />
            </div>
            <div className="nl-bottom-info">
              <span>Teléfono: +569 30103064</span>
              <span>Mail: info@austranet.com</span>
              <span>Dirección: Avenida Providencia 727, oficina 203, Providencia, Santiago</span>
              <a href="https://www.austranet.com" target="_blank" rel="noopener noreferrer">
                https://www.austranet.com
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

NewsletterPreview.displayName = "NewsletterPreview";
export default NewsletterPreview;